import { useCallback, useEffect, useRef, useState } from "react";
import type { ManualLockState } from "@arc/contracts";
import { manualLockApi, releaseManualLockBeacon } from "@/api/client.js";
import { cable, joinRoom } from "@/realtime/cable.js";
import type { AuthSession } from "@/auth/keycloak.js";

/**
 * This TAB's identity for the edit lease (ADR-026).
 *
 * The lease is scoped to a browser SESSION, not a user, so the same author's second tab is a non-holder rather than a
 * co-editor — otherwise both tabs autosave, race the body-version CAS and lose drafts (the `#210` failure). That means
 * this id must be **per tab and stable across re-renders and remounts within the tab**, which is exactly
 * `sessionStorage`: unique per tab, and it survives a reload so a refresh re-affirms the SAME LOCK instead of being
 * refused by the lease it is still holding. (The re-affirm mints a FRESH `leaseId` — see the acquire SQL's R10-F2 note:
 * a preserved one let the unloading page's release beacon delete the reloaded page's lock.)
 */
/** How many consecutive renew failures to tolerate before surrendering — matched to the server's >2x TTL margin. */
const MAX_MISSED_BEATS = 2;

const SESSION_KEY = "arc.lock.session";
/** How long a tab waits for another tab to answer its claim before assuming it is alone. A `BroadcastChannel` message
 *  is delivered on the other context's task queue, so this is milliseconds of slack, not a real delay. */
const CLONE_NEGOTIATION_MS = 150;
const CLAIM_CHANNEL = "arc.lock.session.claim";

function mint(): string {
  return `tab-${crypto.randomUUID()}`;
}

/**
 * Guard against a CLONED session (diff-panel R7-F2).
 *
 * "Duplicate tab" (and open-in-new-tab) **copies sessionStorage**, so a duplicate arrived holding the original's id.
 * The server then matched the same-session re-affirm arm, returned `held_by_me`, and BOTH tabs opened writable — the
 * exact same-user co-editing the session-scoped lease exists to prevent, and it defeated the documented interim
 * protection ("the client honours held_by_other") because the clone never saw `held_by_other`.
 *
 * Each tab announces the id it is using; a tab that hears its OWN id announced by another tab knows it was cloned and
 * re-mints. Both tabs announce, so whichever is second re-mints and is then correctly refused as a non-holder.
 */
function guardAgainstClone(id: string, onRemint: (next: string) => void): { negotiated: Promise<void>; stop: () => void } {
  if (typeof BroadcastChannel === "undefined") return { negotiated: Promise.resolve(), stop: () => undefined };
  const channel = new BroadcastChannel(CLAIM_CHANNEL);
  // Identity + age of THIS tab's guard. `bornAt` is the tiebreak: the tab that started later is the clone.
  const self = crypto.randomUUID();
  const bornAt = Date.now();

  const remint = (): void => {
    const next = mint();
    try {
      sessionStorage.setItem(SESSION_KEY, next);
    } catch {
      /* storage unavailable — the in-memory id is still distinct */
    }
    onRemint(next);
  };

  // A tab that hears its own id from someone else DECIDES rather than blindly yielding, and REPLIES so the other side
  // gets to decide too (diff-panel R9-F1).
  //
  // The first version announced once and yielded on any match. `BroadcastChannel` does not replay earlier posts to a
  // listener that attaches later, so the ORIGINAL tab (which announced before the clone existed) heard the clone, while
  // the clone heard nothing — and the original re-minted. The author's live editor went read-only and stranded its
  // draft while the accidental duplicate silently became the writable one: precisely the inverse of the intent.
  const decide = (theirBornAt: number, theirSelf: string): void => {
    const iAmLater = bornAt > theirBornAt || (bornAt === theirBornAt && self > theirSelf);
    if (iAmLater) remint();
  };

  const onMessage = (ev: MessageEvent<{ kind: "claim" | "reply"; id: string; from: string; bornAt: number }>): void => {
    const msg = ev.data;
    if (!msg || msg.id !== id || msg.from === self) return;
    // Answer a claim so the claimant can compare against us too; a reply needs no further echo (that would loop).
    if (msg.kind === "claim") channel.postMessage({ kind: "reply", id, from: self, bornAt });
    decide(msg.bornAt, msg.from);
    done(); // we have our answer — no need to wait out the window
  };

  // **The caller must not acquire until this settles (diff-panel R11-F2).**
  //
  // The guard used to post its claim and return, with the acquire racing alongside. A duplicated tab therefore acquired
  // under the id it had just inherited, and — since acquire now rotates the lease (R10-F2) — that rotation KILLED the
  // original tab's live lease. The clone then heard the reply, re-minted, and dropped the lease it had just been given:
  // neither tab held a usable one and the manual was stuck until the TTL. Settling first means a clone re-mints BEFORE
  // it acquires, so it is refused as a non-holder (correct) and the original is never touched.
  //
  // Bounded either way: it resolves the moment another tab answers, and otherwise after one short window — a lone tab
  // (the overwhelming case) pays that window once, on entry, and never blocks on a reply that is not coming.
  let done: () => void = () => undefined;
  const negotiated = new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, CLONE_NEGOTIATION_MS);
    done = () => {
      clearTimeout(timer);
      resolve();
    };
  });
  const stop = (): void => {
    done();
    channel.removeEventListener("message", onMessage);
    channel.close();
  };

  channel.addEventListener("message", onMessage);
  channel.postMessage({ kind: "claim", id, from: self, bornAt });
  return { negotiated, stop };
}

function clientSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const minted = mint();
    sessionStorage.setItem(SESSION_KEY, minted);
    return minted;
  } catch {
    // Private mode / storage disabled: fall back to a per-load id. The lease still works; a reload just takes a new one
    // (the old lapses on its TTL), which is strictly better than sharing an id across tabs.
    return mint();
  }
}

/** What the editor needs to know about its hold on the manual. */
export interface ManualLockHold {
  /** `null` while the first acquire is in flight — the editor shows nothing rather than flashing a wrong state. */
  state: ManualLockState | null;
  /** True once we hold the lease: the editor may enable editing. */
  held: boolean;
  /** Set when someone ELSE holds it — the editor refuses entry and shows who. */
  blockedBy: { userId: number; name: string | null } | null;
  /** A transport/permission failure that is neither "held" nor "blocked" (the editor surfaces it rather than lying). */
  error: string | null;
  /**
   * We HELD the lease and then lost it (expired, force-released, handed over, cloned tab) — as opposed to never having
   * been given one (diff-panel R11-F4).
   *
   * The editor keys "read-only" off `error` (fail closed on ANY lock problem — R6-F1) but the *message* off this:
   * telling someone their "edit lock ended" when the acquire was refused 403/409 because the manual is archived or a
   * link describes an event that never happened, right next to the notice correctly explaining why it is read-only.
   */
  lost: boolean;
}

/**
 * Acquire → heartbeat → release the manual's edit lease for as long as the editor is open (ADR-026 Phase 4).
 *
 * - **Acquire on mount.** A `409 MANUAL_LOCKED` is not an error to retry: it is the product answer *"someone else is
 *   editing this"*, so it resolves to `blockedBy` and the editor refuses entry (legacy parity — the old UI showed the
 *   same dialog and aborted).
 * - **Heartbeat on the SERVER-advertised interval.** Never a hardcoded one: the legacy hardcoded 110s against a 120s
 *   lease, a 10-second margin that silently lost the lock on a laptop sleep. The server sizes it with a >2x margin.
 * - **Release on unmount, and on unload via `keepalive`.** The legacy wrote exactly such a beacon and then never called
 *   it, so stale locks were left to expire on the TTL. A normal close frees the manual immediately.
 */
export function useManualLock(
  session: AuthSession | null,
  manualId: number,
  /** Does the editor hold unsaved / in-flight / failed writes? Reported as "active" so a queued hand-over cannot take
   *  the manual out from under work that would be lost (diff-panel R8-F3). */
  hasOutstandingWork?: () => boolean,
): ManualLockHold {
  const [state, setState] = useState<ManualLockState | null>(null);
  const [blockedBy, setBlockedBy] = useState<ManualLockHold["blockedBy"]>(null);
  const [error, setError] = useState<string | null>(null);
  const [lost, setLost] = useState(false);
  // The live lease, kept in a ref so the unmount cleanup and the beacon read the CURRENT values without re-subscribing.
  const lease = useRef<{ sessionId: string; leaseId: string } | null>(null);
  // Kept in a ref so the effect below never re-subscribes when the editor re-renders.
  const outstanding = useRef(hasOutstandingWork);
  outstanding.current = hasOutstandingWork;
  const enabled = session != null && manualId > 0;

  const releaseNow = useCallback(async () => {
    const live = lease.current;
    if (!live) return;
    lease.current = null;
    await manualLockApi.releaseManualLock({ params: { manualId }, body: { clientSessionId: live.sessionId, leaseId: live.leaseId } }).catch(() => undefined);
  }, [manualId]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;
    let sessionId = clientSessionId();
    // A cloned tab must not present the original's id (R7-F2). On re-mint we drop the lease: this tab is NOT the
    // holder, and beating on under the old id would keep the original's lock alive from the wrong tab.
    const cloneGuard = guardAgainstClone(sessionId, (next) => {
      sessionId = next;
      if (lease.current) setLost(true); // only a real loss — a clone that never got a lease has nothing to lose
      lease.current = null;
      setState((prev) => (prev ? { ...prev, status: "held_by_other", leaseId: null } : prev));
      setError("MANUAL_LOCK_LOST");
    });

    // Whether a HUMAN is still working, reported on each beat so the server can tell an active holder (who must not be
    // ejected mid-sentence) from an absent one (who should yield). Also set while writes are outstanding — that is how
    // "never auto-release with unsaved drafts" is honoured, since the server cannot see a client-side buffer.
    let activeSinceBeat = false;
    let consecutiveFailures = 0;
    const markActive = (): void => {
      activeSinceBeat = true;
    };
    for (const ev of ["keydown", "pointerdown"] as const) window.addEventListener(ev, markActive, { passive: true });

    const adopt = (next: ManualLockState): void => {
      setState(next);
      setBlockedBy(null);
      setError(null);
      if (next.leaseId) lease.current = { sessionId, leaseId: next.leaseId };
    };

    const beat = async (): Promise<void> => {
      const live = lease.current;
      if (!live) return;
      const res = await manualLockApi
        .heartbeatManualLock({
          params: { manualId },
          // "Active" is real interaction OR outstanding work: an author who typed and then sat still with a FAILED
          // autosave is exactly the person who must not lose the lock (diff-panel R8-F3).
          body: { clientSessionId: live.sessionId, leaseId: live.leaseId, active: activeSinceBeat || outstanding.current?.() === true },
        })
        .catch(() => null);
      activeSinceBeat = false; // consumed — the next window must earn its own activity
      if (cancelled) return;
      if (res?.status === 200) {
        consecutiveFailures = 0;
        setState(res.body);
        return;
      }
      // A TRANSIENT failure (no response at all: network blip, brief offline, 5xx) must NOT surrender the lease
      // (diff-panel R8-F4). The server's TTL is sized for ~3 missed beats precisely so a blip is survivable; giving up
      // on the first one threw that margin away and locked the author out of their OWN manual until a full reload.
      // Only an explicit rejection, or exhausting the margin, ends the hold.
      // A 5xx is a transient failure too (diff-panel R11-F1). The ts-rest transport resolves ANY HTTP response into
      // `{status, body}` and only yields `null` on a network reject — so a single gravitee 502/504 arrived as
      // `{status: 502}`, skipped this margin entirely and dropped the lease, freezing the author's editor over a blip
      // the >2x TTL margin exists precisely to absorb. Only a 4xx (an explicit "you no longer hold this") is an answer.
      if ((res == null || res.status >= 500) && consecutiveFailures < MAX_MISSED_BEATS) {
        consecutiveFailures += 1;
        return; // keep the timer running and try again on the next tick
      }
      // ANY failed renewal ends the hold — not just an explicit 409 (diff-panel R6-F1).
      //
      // Previously a transient blip cleared the lease and stopped the timer but left `state.status === 'held_by_me'`,
      // so `held` stayed true and the editor stayed WRITABLE on a lease that was no longer being renewed. The author
      // kept typing; ~2 minutes later the server lease expired, someone else acquired, and their saves were rejected by
      // the cross-user fence — trapping the very work this feature exists to protect.
      lease.current = null;
      if (timer) clearInterval(timer);
      const body = res?.status === 409 ? (res.body as { code?: string }) : undefined;
      // Drop out of `held_by_me` so `held` goes false and the editor freezes, whatever the cause.
      setState((prev) => (prev ? { ...prev, status: "held_by_other", leaseId: null } : prev));
      setLost(true); // we were beating, so we HELD it — this is a real loss, not a refusal to grant
      setError(body?.code === "MANUAL_LOCK_LOST" ? "MANUAL_LOCK_LOST" : "LOCK_HEARTBEAT_FAILED");
    };

    // **Learn about a lock change the moment it happens, not on the next beat (diff-panel R10-F1).**
    //
    // `forceRelease` publishes "to tell the ejected holder's editor immediately" — but nothing here listened, so an
    // admin taking the manual left the ejected editor `held_by_me` and WRITABLE for up to a full heartbeat interval.
    // Worse, it was not merely cosmetic: force-release DELETES the row, so `assertNotLockedByAnother` then finds no
    // other holder and the ejected author's body writes SUCCEED — two people editing a manual one of them was
    // explicitly removed from. Version CAS only backstops a blind overwrite; it does not stop the concurrent editing.
    //
    // The push is a "something changed, re-read" signal ONLY — never an ownership answer. Ownership is per SESSION and
    // a broadcast has many recipients, so the authoritative answer comes from the gated `getManualLock`, which knows
    // our session. (`renew` deliberately does not publish, so this costs one GET per real change, not one per beat.)
    let settled = false;
    let reconciling = false;
    let reconcileAgain = false;
    let missedWhileAcquiring = false;
    const reconcile = async (): Promise<void> => {
      if (cancelled) return;
      // Before the first acquire settles, `getManualLock` would answer about a lease we have not taken yet — reading
      // our own acquire echo would flash "unlocked" over an editor that is about to be held. But the push must be
      // REMEMBERED, not discarded (diff-panel R12-F1): a force-release landing in the sub-second window while our
      // acquire was in flight was dropped outright, and since the row is then deleted, `assertNotLockedByAnother`
      // passes and the ejected editor kept saving until its first heartbeat noticed. Replayed on settle below.
      if (!settled) {
        missedWhileAcquiring = true;
        return;
      }
      // COALESCE, never drop (diff-panel R11-F6). A push landing while a re-read is in flight used to return and
      // schedule nothing, so if the dropped one was the force-release whose whole job is to freeze the ejected holder,
      // the editor stayed writable until the next beat — reopening the window R10-F1 closed.
      if (reconciling) {
        reconcileAgain = true;
        return;
      }
      reconciling = true;
      const res = await manualLockApi.getManualLock({ params: { manualId }, query: { clientSessionId: sessionId } }).catch(() => null);
      reconciling = false;
      if (reconcileAgain) {
        reconcileAgain = false;
        void reconcile(); // a change arrived while we were asking; ask again with the newer state
      }
      // A failed re-read is NOT evidence of loss — the heartbeat remains the fence, and freezing on a transient network
      // error is the R8-F4 mistake in another costume.
      if (cancelled || res?.status !== 200) return;
      const next = res.body;
      const heldLocally = lease.current != null;
      setState(next);
      if (next.status === "held_by_me" || !heldLocally) return; // still ours, or we never held it — the notice just refreshed
      // We lost it: force-released, handed over, or taken over after expiry. Freeze NOW.
      lease.current = null;
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
      // Deliberately NOT `setBlockedBy`: that swaps the whole screen for the "being edited by X" notice, and an author
      // who was just ejected mid-sentence needs their unsaved text left on screen. `error` alone makes the editor
      // read-only and shows the lock-lost banner.
      setLost(true);
      setError("MANUAL_LOCK_LOST");
    };
    const onLockChanged = (payload: { manualId?: number }): void => {
      if (payload?.manualId !== manualId) return;
      void reconcile();
    };
    const leaveRoom = joinRoom({ subscribe: "manual:subscribe", unsubscribe: "manual:unsubscribe" }, { manualId });
    cable().on("manual:lock", onLockChanged);

    void (async () => {
      // Settle the clone negotiation FIRST — acquiring under an id we are about to give up is what stranded both tabs.
      await cloneGuard.negotiated;
      if (cancelled) return;
      const acquiringAs = sessionId;
      const res = await manualLockApi.acquireManualLock({ params: { manualId }, body: { clientSessionId: acquiringAs } }).catch(() => null);
      // A re-mint that lands WHILE the acquire is in flight (a third tab, or a reply that arrived late) leaves us
      // holding a lease minted for an id we no longer use: release it rather than adopt it, or the row would sit there
      // held by a session nobody is renewing.
      if (sessionId !== acquiringAs) {
        if (res?.status === 200 && res.body.leaseId) {
          void manualLockApi
            .releaseManualLock({ params: { manualId }, body: { clientSessionId: acquiringAs, leaseId: res.body.leaseId } })
            .catch(() => undefined);
        }
        return;
      }
      if (res?.status === 200) {
        // Unmounted while the acquire was in flight: we now hold a lease nobody will renew, so release it immediately
        // rather than leaving the manual blocked for the full TTL with no editor (diff-panel R8-F6).
        if (cancelled) {
          if (res.body.leaseId) {
            void manualLockApi
              .releaseManualLock({ params: { manualId }, body: { clientSessionId: sessionId, leaseId: res.body.leaseId } })
              .catch(() => undefined);
          }
          return;
        }
        adopt(res.body);
        // The server tells us how often to renew; a client that picks its own interval is how the legacy lost locks.
        timer = setInterval(() => void beat(), Math.max(5, res.body.heartbeatSeconds) * 1000);
        return;
      }
      if (cancelled) return;
      if (res?.status === 409) {
        const body = res.body as { code?: string };
        if (body.code === "MANUAL_LOCKED") {
          // Not a failure — the product answer. Read the holder so the editor can name them.
          const who = await manualLockApi.getManualLock({ params: { manualId }, query: { clientSessionId: sessionId } }).catch(() => null);
          if (cancelled) return;
          setBlockedBy(who?.status === 200 ? (who.body.holder ?? { userId: 0, name: null }) : { userId: 0, name: null });
          if (who?.status === 200) setState(who.body);
          return;
        }
        setError(body.code ?? "MANUAL_NOT_EDITABLE");
        return;
      }
      setError(res?.status === 403 ? "MANUAL_FORBIDDEN" : "LOCK_UNAVAILABLE");
    })().finally(() => {
      settled = true; // from here a push may be reconciled against the server
      if (missedWhileAcquiring) {
        missedWhileAcquiring = false;
        void reconcile(); // a lock change arrived while we were acquiring — ask now that we can interpret the answer
      }
    });

    // Unload: a `keepalive` request survives the page going away, which a normal XHR does not. Without it the manual
    // stays locked until the TTL lapses — the exact stale-lock problem the legacy papered over with its 2-minute lease.
    const onUnload = (): void => {
      const live = lease.current;
      if (!live) return;
      // Routed through the API module: the bearer is attached in the ONE auth seam and never touches a component.
      releaseManualLockBeacon(manualId, { clientSessionId: live.sessionId, leaseId: live.leaseId });
    };
    window.addEventListener("pagehide", onUnload);

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      window.removeEventListener("pagehide", onUnload);
      cable().off("manual:lock", onLockChanged);
      leaveRoom();
      for (const ev of ["keydown", "pointerdown"] as const) window.removeEventListener(ev, markActive);
      cloneGuard.stop();
      void releaseNow();
    };
  }, [enabled, manualId, releaseNow]);

  return { state, held: state?.status === "held_by_me", blockedBy, error, lost };
}
